import React from "react";
import {
  useNavigate,
  useParams,
} from "react-router-dom";

import { useCart } from "../../components/CartContext";

import "./css/CartScreen.css";


function CartScreen() {

  const navigate = useNavigate();

  const {
    restaurantId,
    tableId,
  } = useParams();


  const {
    cart,
    cartCount,
    cartTotal,
    increaseQuantity,
    decreaseQuantity,
    removeFromCart,
    clearCart,
  } = useCart();


  /* =====================================================
     HOME PATH
  ===================================================== */

  const homePath =
    `/${restaurantId}/table/${tableId}`;


  /* =====================================================
     BACK TO MENU
  ===================================================== */

  const handleBackToMenu = () => {

    navigate(homePath);

  };


  /* =====================================================
     CHECKOUT
  ===================================================== */

  const handleContinue = () => {

    if (!cart || cart.length === 0) {
      return;
    }

    navigate(
      `${homePath}/checkout`
    );

  };


  /* =====================================================
     FORMAT PRICE
  ===================================================== */

  const formatPrice = (price) => {

    return Number(
      price || 0
    ).toFixed(2);

  };


  /* =====================================================
     EMPTY CART
  ===================================================== */

  if (!cart || cart.length === 0) {

    return (
      <main className="theme8-cart-page">

        <section className="theme8-cart-empty">

          <div className="theme8-cart-empty-number">
            01
          </div>

          <div className="theme8-cart-empty-icon">
            +
          </div>

          <span className="theme8-cart-label">
            YOUR ORDER
          </span>

          <h1>
            Your cart is empty
          </h1>

          <p>
            Discover our menu and add
            something delicious to your order.
          </p>

          <button
            type="button"
            className="theme8-cart-primary-button"
            onClick={handleBackToMenu}
          >
            Explore Our Menu

            <span>
              →
            </span>

          </button>

        </section>

      </main>
    );

  }


  /* =====================================================
     CART
  ===================================================== */

  return (

    <main className="theme8-cart-page">

      {/* =================================================
          PAGE HEADER
      ================================================= */}

      <section className="theme8-cart-header">

        <div className="theme8-cart-container">

          <button
            type="button"
            className="theme8-cart-back-button"
            onClick={handleBackToMenu}
          >

            <span>
              ←
            </span>

            Back to Menu

          </button>


          <div className="theme8-cart-title-row">

            <div>

              <span className="theme8-cart-label">
                YOUR ORDER
              </span>

              <h1>
                Your Cart
              </h1>

            </div>


            <div className="theme8-cart-table">

              <span>
                TABLE
              </span>

              <strong>
                {tableId}
              </strong>

            </div>

          </div>

        </div>

      </section>


      {/* =================================================
          CART CONTENT
      ================================================= */}

      <section className="theme8-cart-content">

        <div className="theme8-cart-container">

          <div className="theme8-cart-layout">


            {/* =================================================
                CART ITEMS
            ================================================= */}

            <div className="theme8-cart-items-section">

              <div className="theme8-cart-items-heading">

                <div>

                  <span className="theme8-cart-count">
                    {cartCount}
                  </span>

                  <h2>
                    Items in your order
                  </h2>

                </div>


                <button
                  type="button"
                  className="theme8-clear-cart"
                  onClick={clearCart}
                >
                  Clear all
                </button>

              </div>


              {/* =================================================
                  ITEMS
              ================================================= */}

              <div className="theme8-cart-items">

                {cart.map((item) => (

                  <article
                    className="theme8-cart-item"
                    key={item.id}
                  >

                    {/* IMAGE */}

                    <div className="theme8-cart-item-image">

                      {item.image ? (

                        <img
                          src={item.image}
                          alt={item.name}
                        />

                      ) : (

                        <div className="theme8-cart-image-placeholder">
                          +
                        </div>

                      )}

                    </div>


                    {/* DETAILS */}

                    <div className="theme8-cart-item-details">

                      <span className="theme8-cart-item-category">

                        {item.categoryName ||
                          "INDIAN CUISINE"}

                      </span>


                      <h3>
                        {item.name}
                      </h3>


                      {item.description && (

                        <p>
                          {item.description}
                        </p>

                      )}


                      <div className="theme8-cart-item-price">

                        ₹
                        {formatPrice(item.price)}

                      </div>

                    </div>


                    {/* ACTIONS */}

                    <div className="theme8-cart-item-actions">

                      <div className="theme8-quantity-control">

                        <button
                          type="button"
                          onClick={() =>
                            decreaseQuantity(item.id)
                          }
                          aria-label={`Decrease ${item.name}`}
                        >
                          −
                        </button>


                        <span>
                          {item.quantity}
                        </span>


                        <button
                          type="button"
                          onClick={() =>
                            increaseQuantity(item.id)
                          }
                          aria-label={`Increase ${item.name}`}
                        >
                          +
                        </button>

                      </div>


                      <strong className="theme8-cart-item-total">

                        ₹
                        {formatPrice(
                          Number(item.price) *
                          Number(item.quantity)
                        )}

                      </strong>


                      <button
                        type="button"
                        className="theme8-remove-item"
                        onClick={() =>
                          removeFromCart(item.id)
                        }
                      >
                        Remove
                      </button>

                    </div>

                  </article>

                ))}

              </div>

            </div>


            {/* =================================================
                ORDER SUMMARY
            ================================================= */}

            <aside className="theme8-cart-summary">

              <div className="theme8-summary-number">
                02
              </div>


              <span className="theme8-cart-label">
                ORDER SUMMARY
              </span>


              <h2>
                Your order
              </h2>


              {/* SUMMARY ITEMS */}

              <div className="theme8-summary-items">

                {cart.map((item) => (

                  <div
                    className="theme8-summary-item"
                    key={item.id}
                  >

                    <span>

                      {item.quantity}
                      {" × "}
                      {item.name}

                    </span>


                    <strong>

                      ₹
                      {formatPrice(
                        Number(item.price) *
                        Number(item.quantity)
                      )}

                    </strong>

                  </div>

                ))}

              </div>


              <div className="theme8-summary-divider"></div>


              {/* SUBTOTAL */}

              <div className="theme8-summary-row">

                <span>
                  Subtotal
                </span>

                <strong>
                  ₹
                  {formatPrice(cartTotal)}
                </strong>

              </div>


              <p className="theme8-summary-note">

                Taxes and service charges
                will be calculated at checkout.

              </p>


              {/* CHECKOUT */}

              <button
                type="button"
                className="theme8-checkout-button"
                onClick={handleContinue}
              >

                Continue to Checkout

                <span>
                  →
                </span>

              </button>


              {/* ADD MORE */}

              <button
                type="button"
                className="theme8-summary-menu-button"
                onClick={handleBackToMenu}
              >
                ← Add More Items
              </button>

            </aside>

          </div>

        </div>

      </section>


      {/* =================================================
          BOTTOM NOTE
      ================================================= */}

      <section className="theme8-cart-note">

        <div className="theme8-cart-container">

          <span>
            ✦
          </span>

          <p>
            Freshly prepared with authentic
            Indian flavours, just for your table.
          </p>

        </div>

      </section>

    </main>

  );

}


export default CartScreen;